import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { Calculator, Sparkles, TrendingUp, ArrowRight, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Link } from 'react-router-dom';
import { billingAPI } from '../../lib/api';

const fmt = (n) => new Intl.NumberFormat('en-US').format(Math.round(n));

const DEFAULT_VOLUMES = {
  voice_calls: 50, contact_reveals: 100, lead_searches: 50, ai_emails: 500,
  whatsapp_messages: 0, linkedin_actions: 0, ai_scoring: 200, voice_calls_managed: 0,
};

export default function CreditCalculator({ embedded = false, currency = 'USD', usdInrRate = null }) {
  const [levers, setLevers] = useState([]);
  const [volumes, setVolumes] = useState(DEFAULT_VOLUMES);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await billingAPI.getCalculatorData();
        setLevers(res.data?.levers || []);
      } catch (e) {
        toast.error('Could not load pricing data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const estimate = useCallback(async (vols) => {
    try {
      const res = await billingAPI.estimateCredits(vols);
      setResult(res.data);
    } catch (e) {
      // silent — keep last good estimate
    }
  }, []);

  // Debounced live estimate whenever volumes change.
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => estimate(volumes), 300);
    return () => clearTimeout(t);
  }, [volumes, loading, estimate]);

  const setVol = (key, val) => {
    const n = Math.max(0, parseInt(val || '0', 10) || 0);
    setVolumes((v) => ({ ...v, [key]: n }));
  };

  const isINR = currency === 'INR' && usdInrRate;
  // Show local currency for money (credits themselves are currency-neutral).
  const money = (usd) => {
    if (usd == null) return '';
    if (isINR) return `₹${fmt(usd * usdInrRate)}`;
    return `$${fmt(usd)}`;
  };

  const rec = result?.recommended_plan;
  const plans = result?.plans || [];

  const planPrice = useMemo(() => {
    if (!rec) return null;
    if (rec.monthly_price_usd != null) return rec.monthly_price_usd === 0 ? 'Free' : `${money(rec.monthly_price_usd)}/mo`;
    if (rec.starting_price_usd) return `from ${money(rec.starting_price_usd)}/mo`;
    return 'Custom';
  }, [rec, isINR]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="calculator-loading">
        <Skeleton className="h-9 w-80" />
        <div className="grid gap-4 lg:grid-cols-3"><Skeleton className="h-96 lg:col-span-2" /><Skeleton className="h-96" /></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="credit-calculator">
      {!embedded && (
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Calculator className="h-7 w-7 text-indigo-600" />Credit Calculator
          </h1>
          <p className="text-slate-500 mt-1 text-sm max-w-2xl">
            Estimate how many credits your team will use each month, and see which plan fits best.
            1 credit = $0.01. Every AI action bills transparently.
          </p>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Inputs */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Your expected monthly volumes</CardTitle>
            <CardDescription>Enter how many of each you expect to run per month.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            {levers.map((l) => (
              <div key={l.key} className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor={`vol-${l.key}`} className="text-sm font-medium">{l.label}</Label>
                  <span className="text-xs text-slate-400">{l.credits_each} cr each</span>
                </div>
                <Input
                  id={`vol-${l.key}`}
                  type="number"
                  min={0}
                  value={volumes[l.key] ?? 0}
                  onChange={(e) => setVol(l.key, e.target.value)}
                  data-testid={`calc-input-${l.key}`}
                />
                <p className="text-xs text-slate-400">{l.hint}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Result */}
        <div className="space-y-4">
          <Card className="bg-gradient-to-br from-indigo-600 to-violet-600 text-white border-0">
            <CardContent className="pt-6">
              <div className="text-sm text-indigo-100 flex items-center gap-1.5"><TrendingUp className="h-4 w-4" />Estimated monthly usage</div>
              <div className="mt-1 text-4xl font-bold" data-testid="calc-total-credits">{fmt(result?.total_credits || 0)} <span className="text-lg font-normal text-indigo-100">credits</span></div>
              <div className="text-indigo-100 text-sm mt-0.5">≈ {money(result?.total_usd || 0)} in AI usage value / month{isINR ? ` (~$${fmt(result?.total_usd || 0)})` : ''}</div>
            </CardContent>
          </Card>

          {rec && (
            <Card className="border-indigo-200" data-testid="calc-recommended-plan">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-indigo-600" />
                  <CardDescription className="text-indigo-600 font-medium">Recommended plan</CardDescription>
                </div>
                <CardTitle className="text-2xl">{rec.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="text-lg font-semibold text-slate-800">{planPrice}</div>
                <div className="text-sm text-slate-500 flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-emerald-500" />{fmt(rec.included_credits)} credits included / month
                </div>
                {result?.overage_credits > 0 ? (
                  <div className="rounded-lg bg-amber-50 border border-amber-100 p-2.5 text-sm text-amber-700">
                    Your usage exceeds this plan by <b>{fmt(result.overage_credits)}</b> credits
                    (~{money(result.overage_usd)}) — billed as pay-as-you-go overage, or step up a tier.
                  </div>
                ) : (
                  <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-2.5 text-sm text-emerald-700">
                    Comfortably within the included credits. 🎉
                  </div>
                )}
                <Link to="/billing">
                  <Button className="w-full mt-1" data-testid="calc-choose-plan-btn">
                    View plans <ArrowRight className="h-4 w-4 ml-1.5" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Breakdown */}
      {result?.breakdown?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Usage breakdown</CardTitle></CardHeader>
          <CardContent>
            <div className="divide-y divide-slate-100">
              {result.breakdown.map((b) => (
                <div key={b.key} className="flex items-center justify-between py-2 text-sm" data-testid={`calc-breakdown-${b.key}`}>
                  <span className="text-slate-600">{b.label} <span className="text-slate-400">× {fmt(b.quantity)}</span></span>
                  <span className="font-medium text-slate-800">{fmt(b.subtotal_credits)} cr</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan tiers */}
      {plans.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {plans.map((p) => {
            const isRec = rec && p.plan_id === rec.plan_id;
            return (
              <Card key={p.plan_id} className={isRec ? 'border-indigo-400 ring-1 ring-indigo-200' : ''} data-testid={`calc-plan-${p.plan_id}`}>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-slate-800">{p.name}</div>
                    {isRec && <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 border">Best fit</Badge>}
                  </div>
                  <div className="text-2xl font-bold mt-1 text-slate-900">
                    {p.monthly_price_usd != null ? (p.monthly_price_usd === 0 ? 'Free' : money(p.monthly_price_usd)) : (p.starting_price_usd ? `${money(p.starting_price_usd)}+` : 'Custom')}
                    {p.monthly_price_usd ? <span className="text-sm font-normal text-slate-400">/mo</span> : null}
                  </div>
                  <div className="text-sm text-slate-500 mt-1">{fmt(p.included_credits)} credits</div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
